import { useState } from "react"
import { Link, useNavigate, Navigate } from "react-router"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { api, ApiError, setToken, getToken, setUserProfile, setOrganizationsProfile } from "@/lib/api"
import type { UserProfile, OrganisationProfile } from "@/lib/types"
import HomeNav from "@/components/home-nav"
import HomeFooter from "@/components/home-footer"

export default function LoginPage() {
  if (getToken()) return <Navigate to="/dashboard" replace />

  const navigate = useNavigate()

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError("")
    if (!email || !password) {
      setError("Please enter your email and password.")
      return
    }
    setIsLoading(true)
    try {
      const res = await api.post<{ data: { token: string; organizations: OrganisationProfile[] } & UserProfile }>("/auth/login", { email, password })
      const { token, organizations, ...profileData } = res.data
      const userProfile = profileData as UserProfile
      setToken(token)
      setUserProfile(userProfile)
      sessionStorage.removeItem(`email_banner_dismissed_${userProfile.id}`)
      if (Array.isArray(organizations) && organizations.length > 0) {
        setOrganizationsProfile(organizations)
      }
      navigate("/dashboard")
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Login failed. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <HomeNav />
      <div className="flex min-h-[calc(100svh-4rem)]">

        {/* Left — Form panel */}
        <div className="flex flex-1 items-center justify-center bg-white px-6 py-16 sm:px-12">
          <div className="w-full max-w-sm">

            <div className="w-10 h-0.75 bg-secondary mb-8 rounded-full" />
            <h1 className="text-4xl font-black text-[#0D0D0D] tracking-tight leading-[1.05] mb-2">
              Welcome back
            </h1>
            <p className="text-[#0D0D0D]/50 text-sm leading-relaxed mb-10">
              Sign in to access your renewable energy intelligence platform
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <Label
                  htmlFor="email"
                  className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60"
                >
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  className="rounded-none h-11 border-[#0D0D0D]/20 focus-visible:border-[#1D591B] focus-visible:ring-0 bg-white"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label
                    htmlFor="password"
                    className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60"
                  >
                    Password
                  </Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-[#0D0D0D]/45 hover:text-[#1D591B] transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  className="rounded-none h-11 border-[#0D0D0D]/20 focus-visible:border-[#1D591B] focus-visible:ring-0 bg-white"
                />
              </div>

              {error && <p className="text-xs text-destructive">{error}</p>}

              <Button
                className="w-full rounded-none bg-primary hover:bg-foreground text-white h-12 font-semibold text-sm tracking-wide transition-colors duration-200 cursor-pointer mt-2"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? "Signing in…" : "Sign In"}
              </Button>
            </form>

            <p className="text-center text-sm text-[#0D0D0D]/45 mt-8">
              {"Don't have an account? "}
              <Link to="/onboarding" className="text-[#1D591B] hover:underline font-medium">
                Sign up
              </Link>
            </p>

            <p className="text-center text-xs text-[#0D0D0D]/35 mt-6">
              {"By continuing, you agree to Kanu Energy's "}
              <Link to="#" className="underline hover:text-[#0D0D0D]/60">
                Terms of Service
              </Link>
              {" and "}
              <Link to="#" className="underline hover:text-[#0D0D0D]/60">
                Privacy Policy
              </Link>
            </p>

          </div>
        </div>

        {/* Right — Brand panel */}
        <div className="hidden lg:flex w-[48%] bg-[url('/grid.jpg')] bg-cover bg-center bg-no-repeat relative overflow-hidden">
        </div>

      </div>

      <HomeFooter />
    </>
  )
}
